import { useTranslation } from '@doccareplus/shared/lib';
import { cn } from '@doccareplus/shared/lib/utils';
import { Button } from './Button';

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  /** Items per page. */
  pageSize: number;
  /** Total items across all pages. */
  totalCount: number;
  /** Total number of pages. */
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  /** Called with the target 1-based page when the user steps back/forward. */
  onPageChange: (page: number) => void;
  /** Freeze the controls while a page is loading (both buttons disabled). */
  disabled?: boolean;
  className?: string;
}

/**
 * Pagination — shared range summary + previous/next stepper for any paginated
 * list. Consumes the standard paginated envelope fields (page/pageSize/
 * totalCount/totalPages/hasPreviousPage/hasNextPage) so every list paginates
 * identically. Renders nothing when there are no results — the list owns its
 * own empty state.
 *
 * NO business logic — pure presentational; the parent owns the page state.
 */
export function Pagination({
  page,
  pageSize,
  totalCount,
  totalPages,
  hasPreviousPage,
  hasNextPage,
  onPageChange,
  disabled = false,
  className,
}: PaginationProps) {
  const { t } = useTranslation('common');

  if (totalCount <= 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);

  return (
    <nav
      aria-label={t('pagination.label')}
      className={cn(
        'mt-4 flex flex-wrap items-center justify-between gap-3 text-[12.5px] text-[var(--text-3)]',
        className
      )}
    >
      <span>{t('pagination.showing', { from, to, total: totalCount })}</span>
      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost-sm"
            disabled={disabled || !hasPreviousPage}
            onClick={() => onPageChange(page - 1)}
          >
            {t('pagination.previous')}
          </Button>
          <span>{t('pagination.page', { current: page, total: totalPages })}</span>
          <Button
            type="button"
            variant="ghost-sm"
            disabled={disabled || !hasNextPage}
            onClick={() => onPageChange(page + 1)}
          >
            {t('pagination.next')}
          </Button>
        </div>
      )}
    </nav>
  );
}
